import { DBConfig } from "ngx-indexed-db";

/**
 * Raises the configured IndexedDB version to the version already present on disk, if higher.
 *
 * A PWA can serve a stale, cached bundle whose `INDEXED_DB_CONFIG.version` is older than the version a
 * newer bundle (opened in another tab, or previously installed) already wrote to disk. Opening the
 * database with a lower version than what is on disk throws `VersionError` and leaves IndexedDB - and
 * with it all local persistence - unusable until the user manually clears site data. Aligning the
 * configured version upwards before the database is ever opened avoids that failure mode entirely.
 *
 * Must never throw: a bootstrap that depends on this resolving would turn a missing/unsupported API, or a
 * transient failure reading `indexedDB.databases()`, into an app that cannot start at all. Every failure
 * is swallowed (and logged) instead.
 */
export async function alignIndexedDbVersion(config: DBConfig): Promise<void> {
  try {
    if (typeof indexedDB?.databases !== "function") {
      // Not supported (e.g. Firefox < 126) - nothing to align, the existing behavior is unchanged.
      return;
    }

    const databases = await indexedDB.databases();
    const existing = databases.find((database) => database.name === config.name);
    const configuredVersion = config.version ?? 0;

    if (existing?.version !== undefined && existing.version > configuredVersion) {
      config.version = existing.version;
    }
  } catch (error) {
    console.error("Failed to align the configured IndexedDB version with the version present on disk:", error);
  }
}
